import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import Editor from '@draft-js-plugins/editor';
import createLinkifyPlugin from '@draft-js-plugins/linkify';

import { EditorState, RichUtils, getDefaultKeyBinding } from 'draft-js';

// Constants
import ColorConstants from '../../../colors/ColorConstants';

// Types
import { MentionData } from '../plugins/mentions/mentionsPlugin';

// Utils
import { createRichTextEditorState } from '../utils/EditorState.utils';

// Components
import Toolbar from '../Toolbar/Toolbar';
import Link from '../entities/Link';

// Plugins
import createMentionPlugin, { defaultSuggestionsFilter } from '../plugins/mentions/mentionsPlugin';
import createEmojiPlugin from '../plugins/emoji/emojiPlugin';
import createGifPlugin from '../plugins/gif/gifPlugin';
import { smartLinksPlugin, addDocName } from '../plugins/smartLinks/smartLinksPlugin';

// Styles
import './WpRichTextInput.scss';
import 'draft-js/dist/Draft.css';

const DEFAULT_OPTIONS = {
  showToolbar: true,
  showBorder: true,
  transparent: false,
  toolbarPlacement: 'top',
  overflowBehavior: 'grow',
  backgroundColor: ColorConstants.white[0],
};

export type ToolbarPlacement = 'top' | 'bottom';
export type OverflowBehavior = 'grow' | 'scroll';

export type Props = {
  value?: EditorState;
  readOnly?: boolean;
  placeholder?: string;
  disabled?: boolean;
  height?: string;
  autoFocus?: boolean;
  spellCheck?: boolean;
  options?: {
    showBorder?: boolean;
    showToolbar?: boolean;
    transparent?: boolean;
    toolbarPlacement?: ToolbarPlacement;
    overflowBehavior?: OverflowBehavior;
    backgroundColor?: string;
  };
  qaTest?: string;
  onChange?: Function;
  mentionUsers?: MentionData[];
  onFocus?: Function;
  onBlur?: Function;
};

const WpRichTextInput = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  autoFocus = false,
  spellCheck = true,
  readOnly = false,
  height = '175px',
  options,
  qaTest,
  mentionUsers = [],
  onFocus,
  onBlur,
}: Props) => {
  const inputOptions = { ...DEFAULT_OPTIONS, ...options };
  const domEditorRef = useRef<Editor>();
  const emojiMartDropdownRef = useRef(null);
  const gifPickerRef = useRef(null);
  const [editorState, setEditorState] = useState<EditorState>(null);
  const [isGifPickerDropdownOpen, setIsGifPickerDropdownOpen] = useState(false);
  const [isEmojiMartDropdownOpen, setIsEmojiMartDropdownOpen] = useState(false);
  const [isMentionsSuggestionsOpen, setIsMentionsSuggestionsOpen] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState(mentionUsers);

  const isToolbarOnTop = !readOnly && inputOptions.showToolbar && inputOptions.toolbarPlacement === 'top';
  const isToolbarOnBottom = !readOnly && inputOptions.showToolbar && inputOptions.toolbarPlacement === 'bottom';

  const linkifyPlugin = createLinkifyPlugin({ component: Link, target: '_blank' });

  const { MentionSuggestions, mentionsPlugin } = useMemo(() => {
    const mentionPlugin = createMentionPlugin({
      entityMutability: 'IMMUTABLE',
      mentionPrefix: '@',
      supportWhitespace: true,
    });
    // eslint-disable-next-line no-shadow
    const { MentionSuggestions } = mentionPlugin;
    // eslint-disable-next-line no-shadow
    const mentionsPlugin = [mentionPlugin];
    return { mentionsPlugin, MentionSuggestions };
  }, []);

  const { EmojiMartDropdown, emojisPlugin } = useMemo(() => {
    const emojiPlugin = createEmojiPlugin();
    // eslint-disable-next-line no-shadow
    const { EmojiMartDropdown } = emojiPlugin;
    // eslint-disable-next-line no-shadow
    const emojisPlugin = [emojiPlugin];
    return { emojisPlugin, EmojiMartDropdown };
  }, []);

  const { GifPickerDropdown, gifsPlugin } = useMemo(() => {
    const gifPlugin = createGifPlugin();
    // eslint-disable-next-line no-shadow
    const { GifPickerDropdown } = gifPlugin;
    // eslint-disable-next-line no-shadow
    const gifsPlugin = [gifPlugin];
    return { gifsPlugin, GifPickerDropdown };
  }, []);

  const plugins = [smartLinksPlugin, linkifyPlugin, ...mentionsPlugin, ...emojisPlugin, ...gifsPlugin];

  const onIsGifPickerDropdownOpenChange = useCallback(() => {
    setIsGifPickerDropdownOpen(!isGifPickerDropdownOpen);
  }, [isGifPickerDropdownOpen]);

  const onIsEmojiMartOpenChange = useCallback((_open: boolean) => {
    setIsEmojiMartDropdownOpen(_open);
  }, []);

  const onOpenChange = useCallback((_open: boolean) => {
    setIsMentionsSuggestionsOpen(_open);
  }, []);
  const onSearchChange = useCallback(({ value }: { value: string }) => {
    setMentionSuggestions(defaultSuggestionsFilter(value, mentionUsers));
  }, []);

  const handleOnChange = newState => {
    if (onChange) onChange(newState);
    setEditorState(newState);
  };

  const shouldHidePlaceholder = () => {
    // If the user changes block type before entering any text,
    // we hide the the placeholder text

    const contentState = editorState.getCurrentContent();
    if (!contentState.hasText()) {
      if (contentState.getBlockMap().first().getType() !== 'unstyled') {
        return true;
      }
    }
    return false;
  };

  const handleFocusEditor = () => {
    // seems to be a weird issue with initializing plugins
    // if input gets focus on mount
    setTimeout(() => {
      if (!disabled && !readOnly) domEditorRef.current.focus();
      if (onFocus) onFocus();

      // Potential solution to set cursor at end of body.  needs some more investigation
      // if (!isEmpty(editorState)) {
      //   setEditorState((prevEditorState: EditorState) => EditorState.moveFocusToEnd(prevEditorState));
      // }
    }, 50);
  };

  const handleOnBlur = () => {
    if (onBlur) onBlur();
  };

  const handleKeyCommand = command => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleOnChange(newState);
      return true;
    }
    return false;
  };

  const keyBindingFn = event => {
    if (event.key === 'Tab') {
      const newEditorState = RichUtils.onTab(event, editorState, 2);
      handleOnChange(newEditorState);
      return null;
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      if (isMentionsSuggestionsOpen) {
        return undefined;
      }
    }
    return getDefaultKeyBinding(event);
  };

  useEffect(() => {
    if (editorState) addDocName(editorState, handleOnChange);
  }, [editorState]);

  useEffect(() => {
    if (autoFocus) handleFocusEditor();
  }, []);

  useEffect(() => {
    handleOnChange(createRichTextEditorState(value));
  }, [value]);

  if (!editorState) return null;

  const baseClassName = 'WpRichTextInput';
  const classes = classnames(
    baseClassName,
    disabled ? `${baseClassName}--disabled` : false,
    inputOptions.showBorder ? `${baseClassName}--bordered` : false,
    inputOptions.transparent ? `${baseClassName}--transparent` : false,
    `${baseClassName}--toolbar-placement-${inputOptions.toolbarPlacement}`,
    shouldHidePlaceholder() && `${baseClassName}--hide-placeholder`
  );

  let wrapperStyles = {};
  let editorStyles = {};

  if (inputOptions.overflowBehavior === 'grow') {
    wrapperStyles = { minHeight: height };
  }
  if (inputOptions.overflowBehavior === 'scroll') {
    wrapperStyles = { height: height };
    editorStyles = { overflow: 'scroll' };
  }

  wrapperStyles = { ...wrapperStyles, backgroundColor: inputOptions.backgroundColor };

  const ToolbarComponent = (
    <div className={`${baseClassName}__toolbar`}>
      <Toolbar
        editorState={editorState}
        onChange={handleOnChange}
        isEmojiDropdownOpen={isEmojiMartDropdownOpen}
        handleOpenEmojiMartDropdownChange={onIsEmojiMartOpenChange}
        isGifPickerOpen={isGifPickerDropdownOpen}
        handleGifPickerDropdownChange={onIsGifPickerDropdownOpenChange}
        emojiMartDropdownRef={emojiMartDropdownRef}
        gifPickerRef={gifPickerRef}
      />
    </div>
  );

  return (
    <div className={classes} onClick={handleFocusEditor} style={wrapperStyles} data-test={qaTest}>
      {isToolbarOnTop && <>{ToolbarComponent}</>}
      <div className={`${baseClassName}__editor`} style={editorStyles}>
        <Editor
          editorState={editorState}
          onChange={handleOnChange}
          placeholder={placeholder}
          ref={editor => (domEditorRef.current = editor)}
          keyBindingFn={keyBindingFn}
          handleKeyCommand={handleKeyCommand}
          plugins={plugins}
          readOnly={readOnly || disabled}
          spellCheck={readOnly || disabled ? false : spellCheck}
          // blockStyleFn={blockStyleFn}
          onBlur={handleOnBlur}
        />
        <MentionSuggestions
          open={isMentionsSuggestionsOpen}
          onOpenChange={onOpenChange}
          suggestions={mentionSuggestions}
          onSearchChange={onSearchChange}
          onAddMention={() => {
            // get the mention object selected
          }}
        />
        <EmojiMartDropdown
          open={isEmojiMartDropdownOpen}
          onOpenChange={onIsEmojiMartOpenChange}
          emojiMartDropdownRef={emojiMartDropdownRef}
        />
        <GifPickerDropdown
          open={isGifPickerDropdownOpen}
          onOpenChange={onIsGifPickerDropdownOpenChange}
          gifPickerRef={gifPickerRef}
        />
      </div>
      {isToolbarOnBottom && <>{ToolbarComponent}</>}
    </div>
  );
};

export default WpRichTextInput;
